import { trackAmplitudeEvent } from "@/lib/analytics/amplitude";
import { ANALYTICS_EVENTS } from "@/lib/analytics/catalog";
import { captureMessage } from "@/lib/sentry-utils";
import type { TransportRequestConfig } from "@/infrastructure/http/transport-policy";
import {
  isTransportError,
  TransportError,
} from "@/infrastructure/http/transport-error";

export const RECORD_DETAIL_TRANSPORT_CONFIG: TransportRequestConfig = {
  timeoutMs: 60_000,
  maxAttempts: 1,
};

export const RECORD_DETAIL_TIMEOUT_MESSAGE =
  "Loading this report took too long. Please try again.";

export type RecordDetailScope = "private" | "public";

export interface RecordDetailRequestContext {
  scope: RecordDetailScope;
  recordId: string;
  version?: number | null;
  isBackgroundRequest?: boolean;
}

function getCurrentRoute(): string {
  if (typeof window === "undefined") {
    return "server";
  }

  return `${window.location.pathname}${window.location.search}`;
}

export function isRecordDetailTimeoutError(
  error: unknown
): error is TransportError {
  return isTransportError(error) && error.code === "TIMEOUT";
}

export function getRecordDetailErrorMessage(
  error: unknown,
  fallback: string
): string {
  if (isRecordDetailTimeoutError(error)) {
    return RECORD_DETAIL_TIMEOUT_MESSAGE;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallback;
}

export function shouldRetrySharedRecordDetailQuery(): false {
  return false;
}

export function shouldRetryRecordStatusQuery(
  failureCount: number,
  error: unknown
): boolean {
  if (isRecordDetailTimeoutError(error)) {
    return false;
  }

  if (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    error.status === 404
  ) {
    return false;
  }

  return failureCount < 3;
}

function reportRecordDetailTimeout(
  error: TransportError,
  context: RecordDetailRequestContext
): void {
  const payload = {
    component: "aseed-client",
    incident: "record-detail-timeout",
    scope: context.scope,
    record_id: context.recordId,
    version: context.version ?? "latest",
    timeout_ms: RECORD_DETAIL_TRANSPORT_CONFIG.timeoutMs ?? 60_000,
    request_id: error.requestId ?? null,
    is_background_request: context.isBackgroundRequest ?? false,
    path: getCurrentRoute(),
  };

  captureMessage("record_detail_timeout", "warning", payload);
  trackAmplitudeEvent(ANALYTICS_EVENTS.RESULT_RECORD_DETAIL_TIMEOUT, payload);
}

export function normalizeRecordDetailError(
  error: unknown,
  context: RecordDetailRequestContext
): Error {
  if (!isRecordDetailTimeoutError(error)) {
    return error instanceof Error
      ? error
      : new Error("Failed to load report details");
  }

  const normalizedError = new TransportError(RECORD_DETAIL_TIMEOUT_MESSAGE, {
    status: error.status,
    code: error.code,
    retryable: error.retryable,
    requestId: error.requestId,
    cause: error.cause ?? error,
  });

  reportRecordDetailTimeout(normalizedError, context);
  return normalizedError;
}
